/* eslint-disable global-require */
import React, { useEffect, useState, Fragment, useMemo } from "react";

import {
  AppBar,
  Badge,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  SwipeableDrawer,
  Toolbar,
} from "@mui/material";
import { Menu as MenuIcon, Language } from "@mui/icons-material";
import classnames from "classnames";
import jwtDecode from "jwt-decode";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { Link, useHistory, useLocation } from "react-router-dom";

import { AppConfig, PRIVATE_ROUTES, PUBLIC_ROUTES, USER_PERMISSIONS, USER_TOKEN } from "Constants";
import { useAppState } from "Context/StateContext";
import { useViewport } from "Hooks/useViewPort";
import useCurrentBrand from "Hooks/useCurrentBrand";
import LanguageBlock from "../../../layout/Sidebar/LanguageBlock";
import NotificationBlock from "../../../layout/Sidebar/NotificationBlock";
import UserBlock from "../../../layout/Sidebar/UserBlock";
import { NavBarLink } from "Models/nav.bar.link";
import deleteAllTokens from "Util/deleteAllTokens";
import { CompanyProfile, Routes } from "Util/enums";
import { validatesIfTheResourceHasOnlyOneGroup } from "Util/validatesIfTheResourceHasOnlyOneGroup";
import Button, { Color } from "Components/Form/Button";
import { StyledMenu } from "../AnchorMenu";
import ListSwiapleDrawer from "./ListSwiapleDrawer";
import useStyles, {
  LogoutButton,
  LogoutInfoText,
  LogoutInfoTextContainer,
  LogoutInfoTitle,
  LogoutInfoWrapper,
  WarningBadge,
} from "./styles";
import { useQuery } from "@tanstack/react-query";
import UserService from "Services/user.service";
import { useResourcePermissionsActions } from "./useResourcePermissionsActions";
import { COLLAPSED_WIDTH, EXPANDED_WIDTH } from "Components/SideBar/constants";

const BREAKPOINT = 990;

const hideGroupSelected = (url: string) => {
  return url.includes(Routes.PROFILE) || url.includes(Routes.BILLING);
};

export const GlobalHeader: React.FC = () => {
  const { currentBrand } = useCurrentBrand();
  const classes = useStyles({ currentBrand });

  const { width } = useViewport();

  const { t: translate } = useTranslation();

  const {
    currentUser,
    userUid,
    userLogout,
    isLogoutRequired,
    setPathPermissions,
    setUserPermissions,
    userPermissions,
    setLoadingPermission,
  } = useAppState();

  const location = useLocation();
  const history = useHistory();

  const [swipeableIsOpen, setSwipeableIsOpen] = useState<boolean>(false);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [anchorInfo, setAnchorInfo] = useState<null | HTMLElement>(null);
  const [groupToShow, setGroupToShow] = useState<string>();

  const {
    groupSelected,
    navBarLinkSelected,
    navBarLinks,
    setGroupSelected,
    setNavBarLinkSelected,
    showButtonNetwork,
  } = useResourcePermissionsActions();

  const { isLoading } = useQuery(
    ["permissions.get-by-current-user"],
    () => UserService.getUserPermissions(),
    {
      enabled:
        !location.pathname?.includes("update-password") &&
        (!!localStorage.getItem(USER_TOKEN) ||
          userPermissions?.length === 0 ||
          !localStorage.getItem(USER_PERMISSIONS)),
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response?.data) {
          localStorage.setItem(USER_PERMISSIONS, JSON.stringify(response?.data));
          setUserPermissions(response?.data);
        }
      },
    }
  );

  useEffect(() => {
    setLoadingPermission(isLoading);
  }, [isLoading]);

  useEffect(() => {
    const logout = () => {
      userLogout();
      history.replace(PUBLIC_ROUTES.SIGN_IN);
    };

    if (!userUid) {
      return logout();
    }

    const decoded: { timeout: string } = jwtDecode(userUid);

    const { timeout } = decoded;

    if (!timeout) {
      return logout();
    }

    const timeoutUTC = moment(timeout).utc();
    const actual = moment(new Date()).utc();

    if (moment(actual).isAfter(timeoutUTC)) {
      logout();
    }
  }, [userUid]);

  const isNetworkButtonIsVisible = useMemo(() => {
    switch (currentUser?.company?.profile?.initials) {
      case CompanyProfile.ST: {
        if (currentUser?.company?.allowAssociateAgent && showButtonNetwork) return true;

        return false;
      }
      default: {
        if (showButtonNetwork) return true;

        return false;
      }
    }
  }, [currentUser, showButtonNetwork]);

  const shouldHideGroupSelected = useMemo(() => {
    return hideGroupSelected(window.location.href);
  }, [window]);

  const openMenu = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    setAnchorEl(event.currentTarget);
  };

  const closeMenu = () => {
    setAnchorEl(null);
  };

  const openInfo = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    setAnchorInfo(event.currentTarget);
  };

  const closeInfo = () => {
    setAnchorInfo(null);
  };

  const redirectToNavBarLink = (navBarLink: NavBarLink) => {
    history.push(navBarLink.path);
    setSwipeableIsOpen(false);
    setAnchorEl(null);
    setAnchorInfo(null);
  };

  const renderNavBarLinks = () => {
    if (!navBarLinks) return;

    const groups = navBarLinks ? Object.keys(navBarLinks) : [];

    if (!groups?.length) return null;

    return groups.map((group) => {
      return (
        <Fragment key={group}>
          <div
            data-cy={`nav-${translate(group)}`}
            className={
              group === groupSelected && !shouldHideGroupSelected
                ? classes.containerTitleSelected
                : classes.containerTitle
            }
            onClick={(event) => {
              if (validatesIfTheResourceHasOnlyOneGroup(navBarLinks, group)) {
                redirectToNavBarLink(navBarLinks[group][0]);

                return;
              }

              openMenu(event);
              setGroupToShow(group);
            }}
            onMouseEnter={(event) => {
              if (group.toLowerCase() === Routes.SETTINGS && isLogoutRequired) {
                openInfo(event);
              }
            }}
          >
            <span
              className={
                groupSelected === group && !shouldHideGroupSelected
                  ? classnames(classes.title, classes.titleSelected)
                  : classnames(classes.title, classes.titleGray)
              }
            >
              {translate(group)}
            </span>
          </div>
          {group.toLowerCase() === Routes.SETTINGS && isLogoutRequired && <WarningBadge />}
        </Fragment>
      );
    });
  };

  const renderMenuItems = () => {
    if (!groupToShow || !navBarLinks) return;
    const navBarLinksToShow = navBarLinks[groupToShow];

    if (!navBarLinksToShow?.length) return null;

    return navBarLinksToShow?.map((navBarLinkToShow) => {
      return (
        <MenuItem
          data-cy={navBarLinkToShow?.menuTitle.toLowerCase()}
          key={navBarLinkToShow?.menuTitle}
          selected={navBarLinkToShow.path === navBarLinkSelected?.path}
          onClick={() => redirectToNavBarLink(navBarLinkToShow)}
        >
          {translate(navBarLinkToShow?.menuTitle)}
        </MenuItem>
      );
    });
  };

  const renderMenu = () => {
    if (!groupToShow || !navBarLinks || !navBarLinks[groupToShow]) return null;

    return (
      <StyledMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          style: {
            marginTop: "7px",
          },
        }}
        selectedColor={currentBrand?.primaryColor}
        hoverColor={currentBrand?.primaryColor}
      >
        {renderMenuItems()}
      </StyledMenu>
    );
  };

  const renderLogoutInfo = () => {
    if (!isLogoutRequired) return null;

    return (
      <StyledMenu
        anchorEl={anchorInfo}
        open={Boolean(anchorInfo)}
        onClose={closeInfo}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          style: {
            marginTop: "7px",
          },
        }}
      >
        <LogoutInfoWrapper>
          <LogoutInfoTextContainer>
            <LogoutInfoTitle>{translate("someChangesWereNotApplied")}</LogoutInfoTitle>
            <LogoutInfoText>
              {translate("youNeedToLogOutAndLogBackInForSomeChangesToBeApplied")}
            </LogoutInfoText>
          </LogoutInfoTextContainer>
          <LogoutButton
            onClick={() => {
              userLogout();
              history.replace(PUBLIC_ROUTES.SIGN_IN);
            }}
            customColor={Color.DEFAULT}
          >
            {translate("logout")}
          </LogoutButton>
        </LogoutInfoWrapper>
      </StyledMenu>
    );
  };

  const renderNetworkButton = () => {
    if (!isNetworkButtonIsVisible) return null;

    return (
      <div className={classes.itemAction}>
        <Button
          onClick={() => {
            setGroupSelected("network");
            setNavBarLinkSelected(undefined);
            history.push(PRIVATE_ROUTES.NETWORK);
          }}
          className={
            groupSelected === "network" && !shouldHideGroupSelected
              ? classes.buttonSelected
              : undefined
          }
          startIcon={<Language fontSize="medium" />}
          customColor={Color.SECONDARY}
        >
          {translate("network")}
        </Button>
      </div>
    );
  };

  const renderActions = () => {
    if (BREAKPOINT < width) {
      return (
        <>
          {renderNetworkButton()}
          <div>
            <LanguageBlock />
          </div>
          <div>
            <NotificationBlock
              handleGoToNotificationsScreen={() => {
                setGroupSelected(undefined);
                setNavBarLinkSelected(undefined);
                history.push(PRIVATE_ROUTES.NOTIFICATIONS);
              }}
              handleSelected={() => {
                setGroupSelected(undefined);
                setNavBarLinkSelected(undefined);
              }}
            />
          </div>
          <div>
            <UserBlock />
          </div>
        </>
      );
    }

    return (
      <IconButton onClick={() => setSwipeableIsOpen(!swipeableIsOpen)}>
        <MenuIcon htmlColor="#15375C" />
      </IconButton>
    );
  };

  const renderContainerNavBarLinks = () => {
    if (BREAKPOINT < width) {
      return <div className={classes.containerNavBarLink}>{renderNavBarLinks()}</div>;
    }

    return null;
  };

  const renderListSwiapleDrawer = () => {
    if (!navBarLinks) return null;

    const groups = navBarLinks ? Object.keys(navBarLinks) : [];

    if (!groups?.length) return null;

    return groups.map((group) => {
      return (
        <ListSwiapleDrawer
          key={group}
          navBarLinkSelected={navBarLinkSelected}
          group={group}
          groupSelected={groupSelected}
          navBarLinks={navBarLinks[group]}
          redirectToNavBarLink={redirectToNavBarLink}
        />
      );
    });
  };

  const renderListItemNetwork = () => {
    if (!showButtonNetwork) return null;

    return (
      <ListItem
        button
        className={
          groupSelected === "network" && !shouldHideGroupSelected ? classes.itemSelected : undefined
        }
        onClick={() => {
          history.push(PRIVATE_ROUTES.NETWORK);
          setSwipeableIsOpen(false);
          setGroupSelected("Network");
          setNavBarLinkSelected(undefined);
        }}
      >
        <ListItemText primary={translate("network")} />
      </ListItem>
    );
  };

  const renderLogo = () => {
    return (
      <img
        src={
          currentBrand?.enableCustomLogo ? currentUser?.company?.logo?.mediaLink : AppConfig.appLogo
        }
        className={classes.image}
        alt={currentBrand?.enableCustomLogo ? "Company Logo" : "Shipeezi Logo"}
      />
    );
  };

  const renderBadge = () => {
    return (
      <Badge
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        classes={{ badge: classes.customBadge }}
        badgeContent={
          <span className={classes.companyProfileName}>{currentUser?.company?.profile?.name}</span>
        }
      >
        {renderLogo()}
      </Badge>
    );
  };

  const renderListItemLogout = () => {
    return (
      <ListItem
        button
        onClick={() => {
          deleteAllTokens();
          window.location.replace("/");
        }}
      >
        <ListItemText primary={translate("logout")} />
      </ListItem>
    );
  };

  const renderSwipeableDrawer = () => {
    return (
      <SwipeableDrawer
        anchor="top"
        open={swipeableIsOpen}
        onClose={() => setSwipeableIsOpen(false)}
        onOpen={() => setSwipeableIsOpen(true)}
        SwipeAreaProps={{
          className: classes.swipeArea,
        }}
      >
        <List component="nav">
          {renderListSwiapleDrawer()}
          <Divider />
          {renderListItemNetwork()}
          {renderListItemLogout()}
        </List>
      </SwipeableDrawer>
    );
  };

  if (
    window.location.href.includes("signin") ||
    window.location.href.includes("onboarding") ||
    window.location.href.includes(Routes.ESTIMATE_QUOTE)
  ) {
    return null;
  }

  return (
    <div className={classes.root}>
      {renderSwipeableDrawer()}

      <AppBar className={classes.appBar} elevation={0}>
        <Toolbar className={classes.toolbar}>
          {renderLogoutInfo()}
          {renderMenu()}
          <div
            className={classes.container}
            style={{
              transition: "margin-left 0.3s ease, width 0.3s ease",
            }}
          >
            <div className={classes.containerLogo}>
              <Link className={classes.link} to="/" data-cy="logo-link">
                {currentBrand?.enableCustomLogo ? renderLogo() : renderBadge()}
              </Link>
              {renderContainerNavBarLinks()}
            </div>
            <div className={classes.containerActions}>{renderActions()}</div>
          </div>
        </Toolbar>
      </AppBar>
    </div>
  );
};
